import { API_BASE_URL, PERMISSIONS } from "../configuration/config";
import { StatusCodes } from "http-status-codes";
import * as cache from "memory-cache";
import { BaseService } from "./base-service";


const PERMISSIONS_CACHE_KEY = "PERMISSIONS";
const PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS = 1 * 60 * 60 * 1000; // 1 hour

/**
 * Implements user Validation, Authorization and Authentication.
 */
export class UserPermissions extends BaseService {
  /**
   * Singleton instance with the current user permissions.
   */
  public static instance = new UserPermissions(API_BASE_URL);

  private _username: string | null = null;
  private refreshPromise: Promise<void> | null = null;
  private _permissions: PERMISSIONS[] | null = null;

  /**
   * Retrieves the username of the currently authenticated user.
   */
  public get username(): string {
    return this._username ? this._username : "username unavailable";
  }

  /**
   * Retrieves the list of permissions authorized to the current user.
   */
  public get permissions(): PERMISSIONS[] {
    const permissions = cache.get(PERMISSIONS_CACHE_KEY) as PERMISSIONS[];
    if (!permissions) {
      throw new Error("The user has not been authorized.");
    }

    return permissions;
  }

  /**
   * Identifies whether or not the current used has been through the authorization process, irrespective of the result.
   * @returns True if the user has been through the authorization process.
   */
  public get hasAuthorized(): boolean {
    return cache.get(PERMISSIONS_CACHE_KEY) != null;
  }

  /**
   * Identifies whether or not the current user has a given permission.
   * @param permission The permission to check.
   * @param preferReturn Optional boolean parameter to specify return while permissions are refreshing. Defaults to false.
   * @returns True if the user has the specified permission, or preferReturn value if permissions are expired.
   */
  public hasPermission(permission: PERMISSIONS, preferReturn: boolean = false): boolean {
    const cachedPermissions = cache.get(PERMISSIONS_CACHE_KEY) as PERMISSIONS[];

    // Use cached permissions if available
    if (cachedPermissions) {
      this._permissions = cachedPermissions;
      return cachedPermissions.includes(permission);
    }

    // If cache is expired but there are previous permissions, will use them while refreshing
    if (this._permissions) {
      this.refreshPermissionsSilent().catch(() => { });
      return this._permissions.includes(permission);
    }

    // No permissions available, trigger refresh and return preferReturn
    this.refreshPermissionsSilent().catch(() => { });
    return preferReturn;
  }

  /**
   * Executes authorization (and indirectly authentication) for the current user.
   */
  public async authorize() {
    if (!cache.get(PERMISSIONS_CACHE_KEY)) {
      const permissions = await this.callPermissionsService(false);
      this._permissions = permissions;
      cache.put(
        PERMISSIONS_CACHE_KEY,
        permissions,
        PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS
      );
    }
  }

  /**
   * Refreshes user permissions asynchronously in the background.
   * Makes a request to fetch latest permissions and updates the cache.
   * If authentication has expired, this will trigger a step-up auth flow.
   * 
   * @returns {Promise<void>} Promise that resolves when permissions refresh is complete
   * @throws {Error} If the permissions refresh request fails
   */
  public async refreshPermissionsSilent(): Promise<void> {
    // Return existing promise if one is in progress
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Create new promise and store it
    this.refreshPromise = (async () => {
      try {
        const response = await this.fetch("/user-permissions");

        if (response.status === 200) {
          const jsonResponse = await response.json();
          if (!jsonResponse.failureReason) {
            this._username = jsonResponse.username;
            const newPermissions = jsonResponse.allowedFeatures as PERMISSIONS[];

            // Update both cache and private state with fresh permissions
            this._permissions = newPermissions;
            cache.put(
              PERMISSIONS_CACHE_KEY,
              newPermissions,
              PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS
            );
          }
        }
      } catch (error) {
        // If silent refresh fails, the error will be handled by SentryFetch
        throw error;
      } finally {
        // Clear promise when done
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Calls the permissions service to retrieve user permissions
   * 
   * @param forceRefresh - If true, bypasses browser cache and forces a fresh request
   * @returns Promise resolving to array of user permissions
   * @throws Error if authorization fails or service returns failure reason
   */
  private async callPermissionsService(forceRefresh: boolean = false): Promise<PERMISSIONS[]> {
    const headers: Record<string, string> = {};
    if (forceRefresh) {
      headers["Cache-Control"] = "no-cache";
      headers["Pragma"] = "no-cache";
    }

    const response = await this.fetch("/user-permissions", {
      headers,
      cache: forceRefresh ? "no-cache" : "default"
    });

    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred when authorizing the user.");
    }
    const jsonResponse = await response.json();
    if (jsonResponse.failureReason) {
      throw new Error(jsonResponse.failureReason);
    }
    this._username = jsonResponse.username;
    return jsonResponse.allowedFeatures as PERMISSIONS[];
  }
}
